/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DynamicRoutingPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Calls Routing module.
----------------------------------------------------------------*/


import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useNavigate } from 'react-router-dom';
import { Route as RouteIcon, Settings, Clock, Trash2, Plus } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useNotifications } from '../../contexts/NotificationContext';
import { api } from '../../lib/api';
import { ConfigurationTab } from './ConfigurationTab';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { format } from 'date-fns';

interface DynamicRoutingConfig {
    digitsMatch?: string | null;
    deleteUsedRecords: boolean;
    expirationMinutes: number;
    onlyKeepMissedCalls: boolean;
}

interface DynamicRouteEntry {
    id: number;
    uuid?: string;
    token: string;
    extension: string;
    expiresAt: string;
    createdAt: string;
}

export const DynamicRoutingPage = () => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();

    // Mode state: 'list' (default) or 'config'
    const [isConfigMode, setIsConfigMode] = useState(false);

    // --- Config State ---
    const [config, setConfig] = useState<DynamicRoutingConfig | null>(null);
    const [originalConfig, setOriginalConfig] = useState<DynamicRoutingConfig | null>(null);
    const [isConfigLoading, setIsConfigLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // --- List State ---
    const [listData, setListData] = useState<DynamicRouteEntry[]>([]);
    const [isListLoading, setIsListLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; item: DynamicRouteEntry | null }>({
        isOpen: false,
        item: null
    });

    // =========================================================================
    // CONFIGURATION LOGIC
    // =========================================================================

    const loadConfig = useCallback(async () => {
        try {
            setIsConfigLoading(true);
            try {
                const response = await api.get('/api/telephony/dynamic-routing/config');
                const configData = response.data?.data || response.data;
                if (configData) {
                    setConfig(configData);
                    setOriginalConfig(configData);
                }
            } catch (error) {
                console.error('[DynamicRouting] Error loading config:', error);
                const defaultConfig = {
                    digitsMatch: '7',
                    deleteUsedRecords: true,
                    expirationMinutes: 60,
                    onlyKeepMissedCalls: true
                };
                setConfig(defaultConfig);
                setOriginalConfig(defaultConfig);
                showError(t('dynamic_routing.common.error_loading', 'Error loading configuration'));
            }
        } finally {
            setIsConfigLoading(false);
        }
    }, [showError, t]);

    // Load config only when entering config mode
    useEffect(() => {
        if (isConfigMode) {
            loadConfig();
        }
    }, [isConfigMode, loadConfig]);

    const hasChanges = config && originalConfig && (
        config.digitsMatch !== originalConfig.digitsMatch ||
        config.deleteUsedRecords !== originalConfig.deleteUsedRecords ||
        config.expirationMinutes !== originalConfig.expirationMinutes ||
        config.onlyKeepMissedCalls !== originalConfig.onlyKeepMissedCalls
    );

    const handleSave = useCallback(async (action?: 'save' | 'save-and-close' | 'save-and-new') => {
        if (!config) return;

        try {
            setIsSaving(true);
            await api.put('/api/telephony/dynamic-routing/config', config);
            showSuccess(t('dynamic_routing.common.saved_successfully', 'Saved successfully'));
            setOriginalConfig(config);

            if (action === 'save-and-close') {
                setIsConfigMode(false); // Return to list view
            }
        } catch (error) {
            console.error('Failed to save config:', error);
            const msg = error instanceof Error ? error.message : t('dynamic_routing.common.error_saving', 'Error saving configuration');
            showError(msg);
        } finally {
            setIsSaving(false);
        }
    }, [config, showSuccess, showError, t]);

    const handleCancelConfig = useCallback(() => {
        if (originalConfig) {
            setConfig({ ...originalConfig });
        }
    }, [originalConfig]);

    const updateConfig = useCallback(<K extends keyof DynamicRoutingConfig>(field: K, value: DynamicRoutingConfig[K]) => {
        setConfig(prev => prev ? { ...prev, [field]: value } : null);
    }, []);

    // =========================================================================
    // LIST LOGIC
    // =========================================================================

    const loadListEntries = async () => {
        try {
            setIsListLoading(true);
            const response = await api.get('/api/telephony/dynamic-routing/entries', {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    search: searchQuery,
                    sortBy,
                    sortOrder
                }
            });

            const responseData = response.data?.data || response.data;

            if (responseData && (Array.isArray(responseData) || Array.isArray(responseData.data))) {
                const items = Array.isArray(responseData) ? responseData : responseData.data;
                setListData(items);

                if (responseData.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: responseData.pagination.total,
                        totalPages: responseData.pagination.totalPages
                    }));
                } else if (Array.isArray(responseData)) {
                    setPagination(prev => ({ ...prev, total: responseData.length, totalPages: 1 }));
                }
            } else {
                setListData([]);
            }
        } catch (error) {
            console.error('Failed to load entries:', error);
            showError(t('common.error_loading', 'Error loading entries'));
        } finally {
            setIsListLoading(false);
        }
    };

    // Load list when NOT in config mode
    useEffect(() => {
        if (!isConfigMode) {
            loadListEntries();
        }
    }, [isConfigMode, pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

    const handleDeleteClick = (item: DynamicRouteEntry) => {
        setDeleteModal({ isOpen: true, item });
    };

    const confirmDelete = async () => {
        if (!deleteModal.item) return;
        try {
            const idToUse = deleteModal.item.uuid ?? deleteModal.item.id;
            await api.delete(`/api/telephony/dynamic-routing/entries/${idToUse}`);
            showSuccess(t('dynamic_routing.common.deleted_successfully', 'Deleted successfully'));
            loadListEntries();
            setDeleteModal({ isOpen: false, item: null });
        } catch (error) {
            const msg = error instanceof Error ? error.message : t('dynamic_routing.common.error_deleting', 'Error deleting entry');
            showError(msg);
        }
    };

    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

    const handleColumnToggle = useCallback((key: string) => {
        setHiddenColumns(prev =>
            prev.includes(key)
                ? prev.filter(k => k !== key)
                : [...prev, key]
        );
    }, []);

    // ... (rest of logic)

    const columns = useMemo(() => [
        {
            key: 'token',
            label: t('dynamic_routing.token', 'Caller ID'),
            visible: !hiddenColumns.includes('token'),
            sortable: true,
            render: (value: string) => (
                <span className="font-medium text-primary">{value || '-'}</span>
            )
        },
        {
            key: 'extension',
            label: t('dynamic_routing.extension', 'Extension'),
            visible: !hiddenColumns.includes('extension'),
            sortable: true,
            render: (value: string) => value || '-'
        },
        {
            key: 'expiresAt',
            label: t('dynamic_routing.expires_at', 'Expires At'),
            visible: !hiddenColumns.includes('expiresAt'),
            sortable: true,
            render: (value: string) => {
                if (!value) return '-';
                try {
                    return format(new Date(value), 'PPpp');
                } catch (e) {
                    return value;
                }
            }
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            visible: true,
            sortable: false,
            width: '100px',
            align: 'right' as const
        }
    ], [t, hiddenColumns]);

    const renderActions = (item: DynamicRouteEntry) => (
        <div className="flex items-center justify-end gap-2">
            <SimpleTooltip content={t('common.delete', 'Delete')} position="top">
                <button
                    onClick={() => handleDeleteClick(item)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    );

    // =========================================================================
    // RENDER
    // =========================================================================

    if (isConfigMode) {
        return (
            <div className="p-6 pb-20">
                <ModuleFormHeader
                    icon={<Settings className="h-5 w-5 text-primary" />}
                    title={t('dynamic_routing.configuration', 'Configuration')}
                    backButtonText={t('common.list', 'List')}
                    onBack={() => setIsConfigMode(false)}
                />

                <div className="mt-8">
                    <ConfigurationTab
                        config={config}
                        isLoading={isConfigLoading}
                        onConfigChange={updateConfig}
                    />
                </div>

                <FixedActionBar
                    onSaveWithAction={handleSave}
                    onCancel={handleCancelConfig}
                    isSaving={isSaving}
                    saveDisabled={isSaving}
                    cancelText={t('common.cancel', 'Cancel')}
                    useSaveDropdown={true}
                    availableSaveActions={['save', 'save-and-close']}
                    saveDropdownStorageKey="dynamicRoutingSaveAction"
                />
            </div>
        );
    }

    return (
        <div className="p-6">
            <DataGridSimple
                title={t('dynamic_routing.title', 'Dynamic Routing')}
                titleIcon={<RouteIcon className="h-5 w-5 text-primary" />}
                data={listData}
                loading={isListLoading}
                columns={columns}
                onColumnToggle={handleColumnToggle}
                pagination={pagination}
                onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                onItemsPerPageChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
                searchQuery={searchQuery}
                searchPlaceholder={t('dynamic_routing.search_placeholder', 'Search by caller ID or extension...')}
                onSearch={setSearchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(column) => {
                    if (sortBy === column) {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                        setSortBy(column);
                        setSortOrder('asc');
                    }
                }}
                renderActions={renderActions}
                emptyMessage={t('dynamic_routing.no_pending_routes', 'No pending routes found')}
                actions={
                    <SimpleTooltip content={t('common.add', 'Add')} position="top">
                        <button
                            onClick={() => setIsConfigMode(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('common.add', 'Add')}
                        </button>
                    </SimpleTooltip>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, item: null })}
                onConfirm={confirmDelete}
                title={t('common.delete_confirmation', 'Delete Confirmation')}
                message={t('dynamic_routing.confirm_delete', 'Are you sure you want to remove this pending route?')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
            />
            {/* Standard "Exit" button floating or in header? DataGridSimple usually doesn't have "Back". 
                The user wants "Module List". Standard Modules HAVE a way to exit. 
                Ah, DataGridSimple has NO back button. But standard Pages like `ExtensionsPage` usually rely on Sidebar or Breadcrumbs. 
                However, user ASKED for "Exit" button in previous turns. 
                If I use DataGridSimple as main page, I lose the ModuleFormHeader with "Exit".
                BUT `ExtensionsPage` DOES NOT have an Exit button. It relies on Sidebar.
                The USER asked: "Change the back button text to Exit". That was when we had a Form Header.
                If we switch to "List Mode" (like Extensions), we usually don't have a big Exit button.
                However, I can wrap DataGridSimple or put a Header above/inside it?
                DataGridSimple *is* the page content.
                If I want an "Exit" button, I can add it to `actions`?
                Or just assume the user accepts the "Standard List View" which implies Sidebar navigation.
                If I must keep the Exit button, I can put it in the top or as an action.
                Given the user said "que al ingresar al modulo se vea una lista ...", they want the Standard List behavior.
                Standard List behavior = No "Exit" button usually, unless implemented as a custom action.
                I will add an "Exit" button next to "Configuration" button in the Actions area, just in case, pointing to `/pbx/calls-routing`.
            */}
            <button style={{ display: 'none' }} onClick={() => navigate('/pbx/calls-routing')} id="exit-btn"></button>
            {/* I will add Exit button to actions just to be safe and consistent with previous "Exit" request */}
        </div>
    );
};

